import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  InputOnChangeData,
  Option,
  Spinner,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { createAnnouncement, updateAnnouncement } from "api";
import { ConstantValues } from "common/constants";
import { RichTextField } from "components/cells/RichTextFieldCell";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { ColumnTypes } from "models/enums/ColumnTypes";
import {
  ANNOUNCEMENT_STATUS_LABELS,
  ANNOUNCEMENT_STATUSES,
  ANNOUNCEMENT_TYPE_LABELS,
  AnnouncementStatus,
  AnnouncementType,
} from "models/IAnnouncement";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedDropdownStyles,
} from "theme/sharedStyles";
import {
  addAnnouncement,
  updateAnnouncementInSlice,
} from "../../../redux/announcementsSlice";
import { useStyles } from "./EditAnnouncement.styles";
import { IEditAnnouncementsProps } from "./EditAnnouncement.types";

export const EditAnnouncement = (
  props: IEditAnnouncementsProps,
): JSX.Element => {
  const styles = useStyles();
  const contentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const dropdownStyles = useSharedDropdownStyles();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [title, setTitle] = useState<string>(
    props.announcement?.title || ConstantValues.EMPTY_STRING,
  );
  const [content, setContent] = useState<string>(
    props.announcement?.content || ConstantValues.EMPTY_STRING,
  );
  const [type, setType] = useState<AnnouncementType | "">(
    props.announcement?.type || "",
  );
  const [status, setStatus] = useState<AnnouncementStatus>(
    props.announcement?.enabled
      ? ANNOUNCEMENT_STATUSES.ACTIVE
      : ANNOUNCEMENT_STATUSES.INACTIVE,
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (props.isOpen) {
      setTitle(props.announcement?.title ?? ConstantValues.EMPTY_STRING);
      setContent(props.announcement?.content ?? ConstantValues.EMPTY_STRING);
      setType(props.announcement?.type ?? "");
      setStatus(
        props.announcement?.enabled
          ? ANNOUNCEMENT_STATUSES.ACTIVE
          : ANNOUNCEMENT_STATUSES.INACTIVE,
      );
      setFieldErrors({});
      setIsLoading(false);
    }
  }, [props.isOpen, props.announcement]);

  const handleTitleChange = (
    _ev: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    setTitle(data.value);
    if (fieldErrors.title) {
      setFieldErrors((prev) => ({ ...prev, title: "" }));
    }
  };

  const handleContentChange = (
    _ev: unknown,
    value: string | undefined,
  ): void => {
    setContent(value || ConstantValues.EMPTY_STRING);
    if (fieldErrors.content) {
      setFieldErrors((prev) => ({ ...prev, content: "" }));
    }
  };

  const handleTypeSelect = (value: string): void => {
    setType(value as AnnouncementType);
    if (fieldErrors.type) {
      setFieldErrors((prev) => ({ ...prev, type: "" }));
    }
  };

  const handleStatusSelect = (value: string): void => {
    setStatus(value as AnnouncementStatus);
    if (fieldErrors.status) {
      setFieldErrors((prev) => ({ ...prev, status: "" }));
    }
  };

  const validateFields = (): boolean => {
    const errors: Record<string, string> = {};

    if (!title.trim()) {
      errors.title = "Title is required";
    }

    if (!content.trim()) {
      errors.content = "Content is required";
    }

    if (!type) {
      errors.type = "Type is required";
    }

    if (!status) {
      errors.status = "Status is required";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const handleSave = async (): Promise<void> => {
    if (!validateFields()) {
      return;
    }

    setIsLoading(true);

    try {
      const isEnabled = status === ANNOUNCEMENT_STATUSES.ACTIVE;
      const announcementType = type as AnnouncementType;

      if (props.announcement) {
        const updateData = {
          id: props.announcement.id,
          title,
          content,
          enabled: isEnabled,
          type: announcementType,
        };
        if (props.updateFn) {
          await props.updateFn(updateData);
          props.onSaved?.();
        } else {
          await updateAnnouncement(updateData);
          dispatch(
            updateAnnouncementInSlice({
              ...props.announcement,
              ...updateData,
              updatedAt: Math.floor(Date.now() / 1000),
            }),
          );
        }
      } else {
        const createData = {
          title,
          content,
          type: announcementType,
          enabled: isEnabled,
        };
        if (props.createFn) {
          await props.createFn(createData);
          props.onSaved?.();
        } else {
          const newAnnouncement = await createAnnouncement({
            title,
            content,
            type,
            enabled: isEnabled,
          });
          dispatch(addAnnouncement(newAnnouncement));
        }
      }

      props.onDismiss(true);
    } catch {
      setFieldErrors({
        general: "An error occurred while saving. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hasIncompleteDetails =
    !title.trim() || !content.trim() || !type || !status;

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) props.onDismiss(false);
      }}
    >
      <DialogSurface className={footerStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={() => props.onDismiss(false)}
                disabled={isLoading}
              />
            }
          >
            {props.announcement
              ? `Update ${props.entityLabel ?? "Announcement"}`
              : `Add ${props.entityLabel ?? "Announcement"}`}
          </DialogTitle>
          <DialogContent className={styles.content}>
            <div className={contentStyles.formContainer}>
              {isLoading && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving..." />
                </div>
              )}
              <Field
                label="Title"
                required
                validationState={fieldErrors.title ? "error" : "none"}
                validationMessage={fieldErrors.title}
              >
                <Input
                  id="title"
                  value={title}
                  placeholder={`Enter ${(props.entityLabel ?? "announcement").toLowerCase()} title`}
                  onChange={handleTitleChange}
                  disabled={isLoading}
                />
              </Field>

              <Field
                label="Content"
                required
                validationState={fieldErrors.content ? "error" : "none"}
                validationMessage={fieldErrors.content}
              >
                <RichTextField
                  value={content}
                  editable
                  onChange={handleContentChange}
                  type={ColumnTypes.RichText}
                />
              </Field>

              <div className={styles.fieldRow}>
                <Field
                  label="Type"
                  required
                  validationState={fieldErrors.type ? "error" : "none"}
                  validationMessage={fieldErrors.type}
                  className={styles.fieldFlex}
                >
                  <Dropdown
                    placeholder="Select type"
                    value={
                      type
                        ? ANNOUNCEMENT_TYPE_LABELS[type as AnnouncementType]
                        : ""
                    }
                    selectedOptions={type ? [type] : []}
                    onOptionSelect={(_, data) =>
                      handleTypeSelect(data.optionValue || "")
                    }
                    disabled={isLoading}
                    listbox={{ className: dropdownStyles.listbox }}
                  >
                    {Object.entries(ANNOUNCEMENT_TYPE_LABELS).map(
                      ([key, label]) => (
                        <Option key={key} value={key}>
                          {label}
                        </Option>
                      ),
                    )}
                  </Dropdown>
                </Field>
                <Field
                  label="Status"
                  required
                  validationState={fieldErrors.status ? "error" : "none"}
                  validationMessage={fieldErrors.status}
                  className={styles.fieldFlex}
                >
                  <Dropdown
                    placeholder="Select status"
                    value={ANNOUNCEMENT_STATUS_LABELS[status]}
                    selectedOptions={[status]}
                    onOptionSelect={(_, data) =>
                      handleStatusSelect(data.optionValue || "")
                    }
                    disabled={isLoading}
                    listbox={{ className: dropdownStyles.listbox }}
                  >
                    {Object.entries(ANNOUNCEMENT_STATUS_LABELS).map(
                      ([key, label]) => (
                        <Option key={key} value={key}>
                          {label}
                        </Option>
                      ),
                    )}
                  </Dropdown>
                </Field>
              </div>

              {fieldErrors.general && (
                <div role="alert" className={styles.errorMessage}>
                  {fieldErrors.general}
                </div>
              )}
            </div>
          </DialogContent>
        </DialogBody>
        {!isLoading && (
          <div className={footerStyles.dialogFooter}>
            <DialogFooterAction
              icon={<CheckmarkRegular />}
              onClick={handleSave}
              dimmed={hasIncompleteDetails}
              tooltip="Save"
            />
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
